import axios from 'axios';
import CryptoJS from 'crypto-js';
import { secureTokenStorage } from './secureTokenStorage';
import { corsHandler } from '../utils/corsHandler';

const API_BASE_URL = 'https://staging-api.memicai.com';

// Production-level secure API service with axios
class SecureApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
    this.requestCount = 0;
    this.lastRequestTime = 0;
    this.maxRequestsPerMinute = 60;
    this.requestQueue = [];
    this.csrfToken = this.generateCSRFToken();
    
    // Initialize security monitoring
    this.securityLog = [];
    this.suspiciousActivityCount = 0;
    
    // Configure axios instance
    this.axiosInstance = this.createAxiosInstance();
  }

  // Create axios instance with enhanced CORS configuration
  createAxiosInstance() {
    // Use CORS handler for optimized configuration
    const instance = corsHandler.createCorsOptimizedAxios(this.baseUrl, {
      timeout: 30000, // 30 seconds timeout
      withCredentials: false, // Set to false for CORS requests to different domains
    });

    // Add security-specific request interceptor (CORS handling is done by corsHandler)
    instance.interceptors.request.use(
      (config) => {
        // Add security headers
        const securityHeaders = this.getSecurityHeaders();
        config.headers = {
          ...config.headers,
          ...securityHeaders
        };

        // Log request for security monitoring
        this.logSecurityEvent('api_request_initiated', {
          method: config.method?.toUpperCase(),
          url: config.url,
          headers: Object.keys(config.headers)
        });

        return config;
      },
      (error) => {
        this.logSecurityEvent('request_interceptor_error', { error: error.message });
        return Promise.reject(error);
      }
    );

    // Add additional security response interceptor
    instance.interceptors.response.use(
      (response) => {
        // Log successful response
        this.logSecurityEvent('api_response_success', {
          status: response.status,
          url: response.config.url
        });

        return {
          success: true,
          data: this.sanitizeResponse(response.data),
          headers: response.headers,
          status: response.status
        };
      },
      (error) => {
        // Handle authentication and security errors
        if (error.response) {
          const { status, statusText, data } = error.response;
          
          this.logSecurityEvent('api_response_error', {
            status,
            statusText,
            url: error.config?.url
          });

          if (status === 401) {
            secureTokenStorage.clearAll();
            return Promise.resolve({
              success: false,
              error: 'Authentication expired. Please log in again.',
              authError: true
            });
          } else if (status === 403) {
            return Promise.resolve({
              success: false,
              error: 'Access denied. Insufficient permissions.',
              permissionError: true
            });
          } else if (status === 429) {
            return Promise.resolve({
              success: false,
              error: 'Too many requests. Please slow down.',
              rateLimitError: true
            });
          } else if (status >= 500) {
            return Promise.resolve({
              success: false,
              error: 'Server error. Please try again later.',
              serverError: true
            });
          }

          return Promise.resolve({
            success: false,
            error: data?.message || `Request failed: ${status} ${statusText}`,
            status,
            data
          });
        }

        // Let CORS handler deal with CORS errors, pass through others
        return Promise.reject(error);
      }
    );

    return instance;
  }

  // Generate CSRF token
  generateCSRFToken() {
    return CryptoJS.lib.WordArray.random(256/8).toString();
  }

  // Rate limiting implementation
  checkRateLimit() {
    const now = Date.now();
    const oneMinute = 60 * 1000;
    
    // Reset counter if more than a minute has passed
    if (now - this.lastRequestTime > oneMinute) {
      this.requestCount = 0;
      this.lastRequestTime = now;
    }
    
    if (this.requestCount >= this.maxRequestsPerMinute) {
      this.logSecurityEvent('rate_limit_exceeded', { 
        requests: this.requestCount,
        timeWindow: oneMinute 
      });
      return false;
    }
    
    this.requestCount++;
    return true;
  }

  // Log security events
  logSecurityEvent(event, details = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      event,
      details,
      fingerprint: secureTokenStorage.getBrowserFingerprint?.() || 'unknown',
      userAgent: navigator.userAgent
    };
    
    this.securityLog.push(logEntry);
    
    // Keep only last 100 events to prevent memory issues
    if (this.securityLog.length > 100) {
      this.securityLog = this.securityLog.slice(-100);
    }
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.warn('Security Event:', logEntry);
    }
  }

  // Validate request data
  validateRequest(data) {
    if (!data) return true;
    
    try {
      // Check for potential XSS attacks
      const suspiciousPatterns = [
        /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
        /javascript:/gi,
        /on\w+\s*=/gi,
        /eval\s*\(/gi,
        /document\.cookie/gi
      ];
      
      const dataString = JSON.stringify(data);
      
      for (const pattern of suspiciousPatterns) {
        if (pattern.test(dataString)) {
          this.logSecurityEvent('suspicious_input_detected', { pattern: pattern.toString() });
          return false;
        }
      }
      
      return true;
    } catch (error) {
      this.logSecurityEvent('request_validation_error', { error: error.message });
      return false;
    }
  }

  // Sanitize response data
  sanitizeResponse(data) {
    if (!data) return data;
    
    try {
      // Remove any potentially dangerous properties
      const dangerousKeys = ['__proto__', 'constructor', 'prototype'];
      
      const sanitize = (obj) => {
        if (typeof obj !== 'object' || obj === null) return obj;
        
        if (Array.isArray(obj)) {
          return obj.map(sanitize);
        }
        
        const sanitized = {};
        for (const [key, value] of Object.entries(obj)) {
          if (!dangerousKeys.includes(key)) {
            sanitized[key] = sanitize(value);
          }
        }
        return sanitized;
      };
      
      return sanitize(data);
    } catch (error) {
      this.logSecurityEvent('response_sanitization_error', { error: error.message });
      return data;
    }
  }

  // Encrypt request payload if needed
  encryptPayload(data) {
    try {
      if (process.env.NODE_ENV === 'production' && data) {
        const encryptionKey = secureTokenStorage.generateEncryptionKey?.() || this.csrfToken;
        return CryptoJS.AES.encrypt(JSON.stringify(data), encryptionKey).toString();
      }
      return data;
    } catch (error) {
      this.logSecurityEvent('payload_encryption_error', { error: error.message });
      return data;
    }
  }

  // Get comprehensive security headers
  getSecurityHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': this.csrfToken,
      'X-Timestamp': Date.now().toString(),
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    };

    // Add authentication headers if available
    const authHeaders = secureTokenStorage.getSecurityHeaders?.() || secureTokenStorage.getAuthHeader?.();
    if (authHeaders) {
      Object.assign(headers, authHeaders);
    }

    return headers;
  }

  // Make secure HTTP request using axios
  async makeSecureRequest(endpoint, options = {}) {
    // Check rate limiting
    if (!this.checkRateLimit()) {
      throw new Error('Rate limit exceeded. Please try again later.');
    }

    // Validate request data
    if (options.data && !this.validateRequest(options.data)) {
      throw new Error('Invalid request data detected.');
    }

    try {
      const startTime = performance.now();
      
      const config = {
        url: endpoint,
        method: options.method || 'GET',
        data: options.data,
        params: options.params,
        headers: options.headers || {},
        ...options
      };

      const response = await this.axiosInstance.request(config);
      
      const endTime = performance.now();
      
      // Log slow requests
      if (endTime - startTime > 5000) {
        this.logSecurityEvent('slow_request_detected', { 
          endpoint, 
          duration: endTime - startTime 
        });
      }

      return response;
      
    } catch (error) {
      this.logSecurityEvent('api_request_error', {
        endpoint,
        error: error.message
      });
      
      // If axios interceptor didn't handle it, return error format
      return {
        success: false,
        error: error.message,
        originalError: error
      };
    }
  }

  // HTTP methods with enhanced axios implementation
  async get(endpoint, options = {}) {
    return this.makeSecureRequest(endpoint, {
      method: 'GET',
      ...options
    });
  }

  async post(endpoint, data, options = {}) {
    return this.makeSecureRequest(endpoint, {
      method: 'POST',
      data,
      ...options
    });
  }

  async put(endpoint, data, options = {}) {
    return this.makeSecureRequest(endpoint, {
      method: 'PUT',
      data,
      ...options
    });
  }

  async delete(endpoint, options = {}) {
    return this.makeSecureRequest(endpoint, {
      method: 'DELETE',
      ...options
    });
  }

  // Get security statistics
  getSecurityStats() {
    return {
      requestCount: this.requestCount,
      securityEvents: this.securityLog.length,
      suspiciousActivity: this.suspiciousActivityCount,
      rateLimitRemaining: this.maxRequestsPerMinute - this.requestCount,
      lastSecurityEvent: this.securityLog[this.securityLog.length - 1] || null
    };
  }

  // Test CORS connectivity
  async testCorsConnectivity() {
    return corsHandler.testCorsConnectivity(this.baseUrl, '/api/auth/login');
  }

  // Get CORS diagnostics
  getCorsdiagnostics() {
    return corsHandler.getCorsdiagnostics();
  }

  // Clear CORS history
  clearCorsHistory() {
    corsHandler.clearCorsHistory();
  }

  // Reset security counters (for testing or maintenance)
  resetSecurityCounters() {
    this.requestCount = 0;
    this.lastRequestTime = 0;
    this.securityLog = [];
    this.suspiciousActivityCount = 0;
    this.csrfToken = this.generateCSRFToken();
  }
}

export const secureApiService = new SecureApiService();
export default secureApiService; 